import { createContext, lazy, Suspense, use, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, Swords, X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useHaptics } from '@/hooks/useHaptics';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { useInvioSfidaPiattaforma } from '@/hooks/useInvioSfidaPiattaforma';
import { limuloFailure } from '@/lib/limulo/errors';
import {
  CHALLENGE_REASON_PROFILE_LINK,
  CHIAVE_COLLEGANDO_PROFILO,
  modaliteInviabili,
  type EsitoRifiutoSfida,
} from '@/lib/limulo/challenge';
import { crGameIdsOf } from '@/lib/limulo/code';
import type { AttesaProfili } from '@/lib/limulo/registration';
import { LIMULO_PROFILES_URL } from '@/lib/limulo/routes';
import {
  BACKGAMMON_MODALITA_PREDEFINITA,
  catalogoDellaSfida,
  catalogoRiprovabile,
  modalitaDellaSfida,
  type CatalogoDellaSfida,
  type GiocoDellaSfida,
  type ModalitaDellaSfida,
} from '@/lib/limulo/sfida';
import {
  assiDelBackgammon,
  chiaveDellaScelta,
  scegliSuAsse,
  sceltaAncoraServita,
  sceltaDellaChiave,
  type SceltaAssi,
} from '@/lib/limulo/sfidaBackgammon';
import { backgammonGameIdsOf } from '@/lib/qgammon/lobbyTornei';
import { SfidaAssiBackgammon } from '@/components/social/SfidaAssiBackgammon';
import type { PonteI1317Contesto } from '@/components/backgammon/allenamento/PonteI1317';
import { env } from '@/env';
import { LimuloDegraded } from '@/routes/limulo/LimuloDegraded';
import type { LimuloGameMatchMode } from '@/types/limulo';
import { useGetLimuloGameMatchModesQuery, useGetLimuloGamesQuery } from '@/services/limuloApi';

/**
 * Le tre pastiglie «Riprova» dei fogli (catalogo, backgammon, profili) sono piu' basse (`py-1`) e col testo pieno
 * (`text-fg`) della `neutro` `sm` (`py-1.5`, `text-fg-muted`): due override di PELLE, nominati nella PR-2 (I-353).
 */
const RIPROVA_COMPATTO = 'py-1 text-fg';

/**
 * Cio' che il pannello deve sapere dell'invio della sfida di piattaforma (I-207, lotto invio):
 * `useInvioSfidaPiattaforma` incapsula la mutazione, il pannello riceve solo stato e callback.
 */
export interface InvioSfidaVista {
  stato: 'scelta' | 'inviando' | 'inviata' | 'errore';
  /** Popolato solo con `stato === 'errore'`. */
  esito: EsitoRifiutoSfida | null;
  /** kb#388: l'invio sta aspettando il sync del profilo (con `stato === 'inviando'`): si dice, invece di un pulsante che gira muto. */
  collegando?: boolean;
  /**
   * Lo stato della lista profili PER il gioco scelto (stessa regola dell'iscrizione ai tornei,
   * `attesaProfili`): senza una lista ARRIVATA `onInvia` non manda `profileId: null` — il motore
   * lo leggerebbe come «non hai un profilo» a chi ce l'ha (revisione #487, bloccante).
   */
  statoProfili: (gameId: string) => AttesaProfili;
  onRiprovaProfili: () => void;
  /** Il gioco scelto cambia (`''` = nessuno): serve a chi sincronizza il profilo di quel gioco (I-493). */
  onGiocoScelto: (gameId: string) => void;
  onInvia: (input: { gameId: string; gameModeKey: string }) => void;
  onReset: () => void;
}

interface SfidaSheetProps {
  /** Il nome dell'amico, come lo mostra la riga o il profilo. */
  avversario: string;
  catalogo: CatalogoDellaSfida;
  /**
   * L'invio di piattaforma (I-207): `null` significa che il chiamante non lo offre, e il gioco
   * scelto resta in sola lettura come prima di questo lotto (`modalitaDi`). In produzione
   * `SfidaSheetDaCatalogo` lo passa sempre valorizzato.
   */
  invio?: InvioSfidaVista | null;
  /**
   * Le modalita' del gioco scelto, quando `invio` e' `null`. Le legge il contenitore
   * (`ModalitaDaCatalogo`) cosi' il pannello resta puro. Senza, il pannello dice che arriveranno.
   */
  modalitaDi?: (gioco: GiocoDellaSfida) => ReactNode;
  /**
   * La `key` della modalita' che il gioco scelto ha GIA' selezionata all'apertura (oggi solo il
   * backgammon: 3 punti, rapid). Vale solo se quella `key` e' fra le modalita' INVIABILI: una
   * preselezione che il motore non ha non si inventa, e l'utente sceglie a mano. Una scelta gia'
   * fatta dall'utente la batte sempre.
   */
  modalitaPredefinita?: (gameId: string) => string | null;
  /**
   * Il gioco e' backgammon? (I-459) Se si', con `invio`, le modalita' inviabili si offrono come DUE scelte,
   * «Lunghezza» e «Orologio» (`lib/limulo/sfidaBackgammon.ts`), invece della fila: sempre che la regola le sappia
   * leggere TUTTE, altrimenti resta la fila. Senza, ogni gioco ha la fila di sempre. Lo decide il catalogo
   * (`profileType`, `backgammonGameIdsOf`), mai un `gameId` scritto qui.
   */
  eBackgammon?: (gameId: string) => boolean;
  /**
   * L'uscita quando il backgammon NON e' fra i giochi offerti, o e' offerto ma senza modalita'
   * inviabili, e l'assenza e' DEFINITIVA (risposta arrivata senza, o in errore; mai mentre carica):
   * una riga che dice perche' e «Preferisco un codice», che chiude il pannello e apre il flusso
   * di sempre (`InviteSheet`). `onRiprova` c'e' solo dove l'assenza nasce da un ERRORE delle
   * modalita' (un errore si ritenta, un elenco vuoto no). Solo la lobby backgammon la passa: dal
   * selettore d'amici gli e' rimasto il ripiego, ma il pannello si apre DOPO che si e' chiuso, e
   * senza questa non c'e' un'uscita. Da Amici e' `null` e il pannello resta com'e'.
   */
  uscitaBackgammon?: { onPreferisciCodice: () => void; onRiprova?: () => void } | null;
  /**
   * Il `gameId` gia' scelto all'apertura (T1-6, lobby backgammon): salta lo step «scegli il
   * gioco» per chi arriva sapendo gia' cosa vuole giocare. Arriva async (il catalogo risponde
   * dopo il montaggio): se il catalogo non lo contiene ancora, la preselezione arriva con lui
   * al giro successivo. Non sovrascrive mai una scelta gia' fatta dall'utente.
   */
  giocoIniziale?: string;
  onRiprova: () => void;
  onClose: () => void;
}

/**
 * Il pannello della **sfida a un amico** su un gioco della piattaforma: si sceglie il gioco (I-207).
 * Prende il posto di `ChallengeOptionsSheet` (QDuels, solo Clash Royale) dietro
 * `useSfidaDiPiattaforma`, e ne tiene il guscio: fondo scuro, foglio dal basso, stessi colori.
 *
 * **Due modi di finire**, uguali per ogni gioco (backgammon compreso, dal lotto C1 di T2-3):
 * - con `invio` valorizzato: si sceglie la modalita' (filtrata a `teamSize` che contiene 1: il
 *   motore rifiuta le altre) e si manda la sfida VERA, `POST /v1/challenges` (contratto PR169,
 *   I-207 lotto invio). Chi sfida NON aspetta qui: la sfida si vede in Amici › Sfide ›
 *   Inviate, e la partita, se l'amico accetta, arriva come azione del torneo da 2 nella barra;
 * - senza `invio` (chiamante che non lo offre): le modalita' restano in sola lettura, e il
 *   bottone e' spento (`modalitaDi`) — il comportamento di prima del lotto invio.
 *
 * **Le modalita' della sfida di piattaforma** vengono dal provider del gioco, lette con
 * `GET /v1/games/:gameId/match-modes` (limulo#152, kb#297 domanda 2).
 */
export function SfidaSheet({
  avversario,
  catalogo,
  invio = null,
  modalitaDi,
  modalitaPredefinita,
  eBackgammon,
  uscitaBackgammon = null,
  giocoIniziale,
  onRiprova,
  onClose,
}: SfidaSheetProps) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const [scelto, setScelto] = useState<string | null>(giocoIniziale ?? null);
  const titoloId = useId();
  // Il catalogo arriva DOPO il montaggio: se la preselezione non era ancora pronta al primo
  // render, la prende quando arriva. L'update funzionale (mai sovrascrivere uno `scelto` gia'
  // non-nullo) e' cio' che tiene `scelto` fuori dalle dipendenze: rimetterlo rifarebbe scattare
  // l'effetto a ogni scelta manuale dell'utente.
  useEffect(() => {
    if (!giocoIniziale) return;
    setScelto((corrente) => corrente ?? giocoIniziale);
  }, [giocoIniziale]);
  // Il pannello e' un livello sopra la schermata (#441): il tasto indietro di Telegram lo CHIUDE,
  // con la stessa `onClose` della X. Senza, il tasto cambiava schermata e smontava il pannello,
  // perdendo la pagina. E' montato solo da aperto, quindi il livello e' attivo finche' esiste.
  useLivelloIndietro(true, onClose);

  const giochi = catalogo.kind === 'ok' ? catalogo.giochi : [];
  // Se il catalogo si rilegge e il gioco scelto non c'e' piu', la scelta decade: non si mostra
  // come scelto un gioco che non e' nell'elenco.
  const gioco = giochi.find((g) => g.gameId === scelto) ?? null;
  // Il gioco SCELTO (non quello preselezionato dal catalogo ancora in arrivo, ne' uno decaduto) lo sa chi
  // sincronizza il profilo (I-493): il pannello riporta, non decide.
  const giocoSceltoId = gioco?.gameId ?? '';
  const onGiocoScelto = invio?.onGiocoScelto;
  useEffect(() => {
    onGiocoScelto?.(giocoSceltoId);
  }, [onGiocoScelto, giocoSceltoId]);
  // Mentre una sfida e' in volo il gioco non si cambia: l'invio in corso ha il suo destinatario, e
  // cambiare gioco sotto di lui confonderebbe l'esito.
  const bloccato = invio?.stato === 'inviando';

  const scegli = (gameId: string) => {
    if (bloccato) return;
    haptics.selection();
    setScelto(gameId);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titoloId}
    >
      <button
        type="button"
        aria-label={t('social.duel_cancel', { defaultValue: 'Cancel' })}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-1 flex items-center justify-between">
          <h2 id={titoloId} className="flex items-center gap-2 text-base font-bold text-fg">
            <Swords className="h-4 w-4 text-brand" />
            {t('social.sfida_scegli_gioco', { defaultValue: 'Choose the game' })}
          </h2>
          <Button
            variant="icona"
            size={32}
            tono="neutro"
            onClick={onClose}
            aria-label={t('social.duel_cancel', { defaultValue: 'Cancel' })}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="mb-4 truncate text-sm text-fg-muted">{avversario}</p>

        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
          {t('social.sfida_gioco', { defaultValue: 'Game' })}
        </p>
        <div className="mb-4">
          {catalogo.kind === 'caricamento' && (
            <p className="flex items-center gap-2 text-sm text-fg-muted" role="status">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('social.sfida_giochi_caricamento', { defaultValue: 'Loading games…' })}
            </p>
          )}
          {catalogo.kind === 'errore' && (
            // La frase la sceglie il verdetto (`LimuloDegraded`, la stessa di ogni lista Limulo), e
            // «Riprova» c'e' solo dove riprovare puo' servire (`catalogoRiprovabile`).
            <div className="flex flex-col gap-2" role="alert">
              <LimuloDegraded failure={catalogo.failure} context="list" />
              {catalogoRiprovabile(catalogo.failure) && (
                <Button
                  variant="neutro"
                  size="sm"
                  forma="pastiglia"
                  peso="bold"
                  onClick={onRiprova}
                  className={`self-start ${RIPROVA_COMPATTO}`}
                >
                  {t('social.sfida_riprova', { defaultValue: 'Retry' })}
                </Button>
              )}
            </div>
          )}
          {catalogo.kind === 'ok' && giochi.length === 0 && (
            <p className="text-sm text-fg-muted" role="status">
              {t('social.sfida_giochi_vuoto', { defaultValue: 'No games available yet.' })}
            </p>
          )}
          {giochi.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {giochi.map((g) => (
                <Button
                  key={g.gameId}
                  variant="scelta"
                  tono="incavo"
                  peso="bold"
                  pressed={gioco?.gameId === g.gameId}
                  onClick={() => scegli(g.gameId)}
                  disabled={bloccato && gioco?.gameId !== g.gameId}
                  className="after:h-9"
                >
                  {g.nome}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Dopo una sfida INVIATA la via d'uscita dal backgammon non serve piu': la riga sta SOPRA
            la sezione dell'invio, e col «Sfida inviata» che compare sotto non deve restare una
            frase che parla di un altro gioco. */}
        {uscitaBackgammon !== null && invio?.stato !== 'inviata' && (
          <div className="mb-4 flex flex-col items-start gap-2">
            <p className="text-sm text-fg-muted" role="status">
              {t('social.sfida_backgammon_non_disponibile', {
                defaultValue: "Backgammon can't be challenged from here right now.",
              })}
            </p>
            <div className="flex items-center gap-3">
              {uscitaBackgammon.onRiprova !== undefined && (
                <Button
                  variant="neutro"
                  size="sm"
                  forma="pastiglia"
                  peso="bold"
                  onClick={uscitaBackgammon.onRiprova}
                  className={RIPROVA_COMPATTO}
                >
                  {t('social.sfida_riprova', { defaultValue: 'Retry' })}
                </Button>
              )}
              <Button
                variant="link"
                onClick={() => {
                  haptics.selection();
                  uscitaBackgammon.onPreferisciCodice();
                }}
                className="text-fg-muted underline"
              >
                {t('invite.pickFriendFallback', { ns: 'backgammon' })}
              </Button>
            </div>
          </div>
        )}

        {gioco !== null && invio !== null ? (
          <InvioModalita
            key={gioco.gameId}
            gioco={gioco}
            avversario={avversario}
            invio={invio}
            predefinita={modalitaPredefinita?.(gioco.gameId) ?? null}
            eBackgammon={eBackgammon?.(gioco.gameId) === true}
          />
        ) : (
          <>
            {gioco !== null && (
              <>
                <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
                  {t('social.duel_game_mode', { defaultValue: 'Game mode' })}
                </p>
                {modalitaDi ? (
                  modalitaDi(gioco)
                ) : (
                  <p className="mb-5 text-sm text-fg-muted">
                    {t('social.sfida_modalita_in_arrivo', {
                      defaultValue: 'The {{game}} modes will come with the challenge.',
                      game: gioco.nome,
                    })}
                  </p>
                )}
              </>
            )}
            <Button peso="bold" fullWidth disabled>
              <Swords className="h-4 w-4" />
              {invio === null
                ? t('social.sfida_invio_in_arrivo', { defaultValue: 'Sending coming soon' })
                : t('social.duel_send', { defaultValue: 'Send challenge' })}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Le modalita' di un gioco, come le mostra il pannello quando NON ha un `invio` (il chiamante non
 * lo offre): solo lettura, senza una scelta. Ogni stato ha la sua frase. Sull'errore non c'e'
 * «Riprova»: il caso atteso e' un 500 per un'utenza senza identita' mobile, che non passa
 * riprovando.
 */
export function ModalitaVista({ stato, nome }: { stato: ModalitaDellaSfida; nome: string }) {
  const { t } = useTranslation('general');
  if (stato.kind === 'caricamento') {
    return (
      <p role="status" className="mb-5 flex items-center gap-2 text-sm text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('social.sfida_modalita_caricamento', { defaultValue: 'Loading modes…' })}
      </p>
    );
  }
  if (stato.kind === 'errore') {
    return (
      <p role="alert" className="mb-5 text-sm text-fg-muted">
        {t('social.sfida_modalita_errore', { defaultValue: "Couldn't load the {{game}} modes.", game: nome })}
      </p>
    );
  }
  if (stato.modalita.length === 0) {
    return (
      <p className="mb-5 text-sm text-fg-muted">
        {t('social.sfida_modalita_nessuna', { defaultValue: 'No modes to show for {{game}}.', game: nome })}
      </p>
    );
  }
  return (
    <>
      <ul className="mb-2 flex flex-wrap gap-2">
        {stato.modalita.map((m) => (
          <li key={m.key} className="rounded-full bg-bg-tertiary px-3 py-1.5 text-xs font-bold text-fg-muted">
            {m.nome}
          </li>
        ))}
      </ul>
      <p className="mb-5 text-xs text-fg-muted">
        {t('social.sfida_modalita_nota', { defaultValue: "You'll pick one here when challenges can be sent." })}
      </p>
    </>
  );
}

/**
 * Le modalita' del gioco scelto, lette dal BFF: la query parte solo quando un gioco e' scelto.
 * ⚠️ `currentData`, non `data`: cambiando gioco `data` mostrerebbe le modalita' del gioco di prima
 * (revisione di #463, 1; vedi `modalitaDellaSfida`).
 */
function ModalitaDaCatalogo({ gioco }: { gioco: GiocoDellaSfida }) {
  const q = useGetLimuloGameMatchModesQuery(gioco.gameId);
  return <ModalitaVista stato={modalitaDellaSfida({ data: q.currentData, isError: q.isError })} nome={gioco.nome} />;
}

const modalitaDaCatalogo = (gioco: GiocoDellaSfida) => <ModalitaDaCatalogo gioco={gioco} />;

/**
 * L'esito di un rifiuto di `POST /v1/challenges`, a schermo (dispatch sfide-orcr-3-invio, punto
 * 2). `profilo-richiesto`/`profilo-rifiutato` portano l'invito a crearsi/controllare il profilo: la
 * rotta esiste gia' (`LIMULO_PROFILES_URL`). Il testo del link e' DIVERSO per i due (revisione
 * #487, punto e — `CHALLENGE_REASON_PROFILE_LINK`): «Crealo qui» a chi non ne ha uno,
 * «Controlla i tuoi profili» a chi il motivo puo' colpire anche con un profilo valido.
 */
function RifiutoSfida({ esito }: { esito: EsitoRifiutoSfida }) {
  const { t } = useTranslation('general');
  if (esito.kind === 'nota') {
    const chiaveLink = CHALLENGE_REASON_PROFILE_LINK[esito.motivo];
    return (
      <p className="mb-3 text-sm text-danger" role="alert">
        {t(esito.chiave)}
        {chiaveLink !== undefined && (
          <>
            {' '}
            <Link to={LIMULO_PROFILES_URL} className="font-bold underline">
              {t(chiaveLink)}
            </Link>
          </>
        )}
      </p>
    );
  }
  if (esito.kind === 'profilo') {
    return (
      <p className="mb-3 text-sm text-danger" role="alert">
        {t(esito.chiave, { reason: esito.ragione, code: esito.codice })}
      </p>
    );
  }
  return (
    <p className="mb-3 text-sm text-danger" role="alert">
      {t('social.sfida_rifiuto_generico', {
        defaultValue: "Couldn't send the challenge ({{code}}).",
        code: esito.codice !== '' ? esito.codice : t('social.sfida_rifiuto_sconosciuto', { defaultValue: 'unknown' }),
      })}
    </p>
  );
}

/**
 * Le modalita' INVIABILI di un gioco, come le legge chi sta per inviare: l'invio (`InvioModalita`)
 * e il contenitore, che con la stessa lettura decide se il backgammon ha ancora una via
 * (`uscitaBackgammon`). Il filtro per teamSize va PRIMA di `modalitaDellaSfida`: quella funzione
 * trasforma le modalita' grezze in `{key, nome}` e butta via `teamSize` nel farlo (e' fatta per la
 * lista in sola lettura, che non ne ha bisogno). Qui serve ancora, per scartare cio' che il motore
 * rifiuterebbe comunque. `currentData`, mai `data`: stessa trappola del gioco di prima.
 */
function modalitaInviabiliDi(q: {
  currentData?: readonly LimuloGameMatchMode[];
  isError: boolean;
}): ModalitaDellaSfida {
  const inviabili = q.currentData === undefined ? undefined : modaliteInviabili(q.currentData);
  return modalitaDellaSfida({ data: inviabili, isError: q.isError });
}

/**
 * **L'invio della sfida di piattaforma** (I-207, lotto invio; da C1 anche per il backgammon): le
 * modalita' vengono dallo stesso BFF di `ModalitaDaCatalogo` (`currentData`, mai `data`: stessa
 * trappola del gioco di prima), filtrate a quelle il cui `teamSize` contiene 1 —
 * il motore rifiuta le altre (contratto PR169 §MOTORE). `key={gioco.gameId}` sul chiamante rimonta
 * questo componente a ogni cambio di gioco: la modalita' scelta di prima non ha senso su un altro
 * gioco, e un remount vale come reset senza un effetto che sincronizzi due stati. Il remount NON e'
 * l'unica difesa: anche la scelta a mano si valida contro le modalita' di QUESTO gioco.
 */
// PONTE PROVVISORIO I-1317 (A3: contesto e moduli lazy) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda).
// Tutto SOLO nel letterale del flag del coach: in produzione il contesto e i due lazy sono `null` e il chunk non esiste (P4 = 0). Se il chunk
// non si carica (V18 l): la scelta torna gli assi di sempre, e al posto dell'invio un messaggio e un bottone spento — mai la ricaduta sull'invio.
const PonteI1317Ctx = import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' ? createContext<PonteI1317Contesto | null>(null) : null;
/** Il Provider del contesto del ponte: lo usa A3, e il banco del ponte (il contesto non si esporta). Fuori dal letterale rende i figli. */
export function PonteI1317Fornitore({ value, children }: { value: PonteI1317Contesto; children: ReactNode }) {
  return PonteI1317Ctx ? <PonteI1317Ctx value={value}>{children}</PonteI1317Ctx> : <>{children}</>;
}
export function PonteI1317ChunkFallito() {
  const { t } = useTranslation('general');
  return (
    <>
      <p role="alert" className="mb-3 text-sm text-danger" data-testid="ponte-i1317-chunk-fallito">
        {t('social.challenge_error', { defaultValue: "Couldn't send the challenge. Try again." })}
      </p>
      <Button peso="bold" fullWidth disabled>
        <Swords className="h-4 w-4" />
        {t('social.duel_send', { defaultValue: 'Send challenge' })}
      </Button>
    </>
  );
}
const PonteAssiI1317Lazy =
  import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true'
    ? lazy(() =>
        import('@/components/backgammon/allenamento/PonteI1317')
          .then((m) => ({ default: m.PonteAssiI1317 }))
          .catch(() => ({ default: SfidaAssiBackgammon })),
      )
    : null;
const PonteInvioI1317Lazy =
  import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true'
    ? lazy(() =>
        import('@/components/backgammon/allenamento/PonteI1317')
          .then((m) => ({ default: m.PonteInvioI1317 }))
          .catch(() => ({ default: PonteI1317ChunkFallito })),
      )
    : null;

function InvioModalita({
  gioco,
  avversario,
  invio,
  predefinita,
  eBackgammon,
}: {
  gioco: GiocoDellaSfida;
  avversario: string;
  invio: InvioSfidaVista;
  /** La `key` preselezionata, se il gioco ne ha una (`modalitaPredefinita`); `null` = nessuna. */
  predefinita: string | null;
  /** Il gioco e' backgammon (`eBackgammon` del pannello): le modalita' si offrono come due scelte, se si sanno leggere. */
  eBackgammon: boolean;
}) {
  const { t } = useTranslation('general');
  // PONTE PROVVISORIO I-1317 (A3: lettura del contesto) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda).
  const ponteI1317 = import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' && PonteI1317Ctx ? use(PonteI1317Ctx) : null;
  const q = useGetLimuloGameMatchModesQuery(gioco.gameId);
  const stato = modalitaInviabiliDi(q);
  const modalita = stato.kind === 'ok' ? stato.modalita : [];
  const [sceltaManuale, setSceltaManuale] = useState<string | null>(null);
  // La preselezione e' DERIVATA, non un effetto: le modalita' arrivano dopo il montaggio, e una
  // `key` che il BFF non serve fra le inviabili non si sceglie. Una scelta a mano la batte sempre.
  const valida = (key: string | null) =>
    key !== null && modalita.some((m) => m.key === key) ? key : null;
  // I due assi del backgammon (I-459), dalle STESSE voci inviabili della fila. `null` = niente assi: gioco diverso,
  // modalita' non ancora arrivate, o una voce che la regola non sa leggere ⇒ la fila di sempre, senza letture a meta'.
  const assi =
    eBackgammon && q.currentData !== undefined ? assiDelBackgammon(modaliteInviabili(q.currentData)) : null;
  const [sceltaAssiManuale, setSceltaAssiManuale] = useState<SceltaAssi | null>(null);
  // Come la fila: la preselezione si deriva (3 punti, rapid se servito, altrimenti niente), e una scelta a mano la batte.
  const sceltaAssi =
    assi === null ? null : (sceltaAncoraServita(assi, sceltaAssiManuale) ?? sceltaDellaChiave(assi, predefinita));
  const modalitaScelta =
    assi !== null && sceltaAssi !== null
      ? chiaveDellaScelta(assi, sceltaAssi)
      : (valida(sceltaManuale) ?? valida(predefinita));

  // Il reset all'apertura di un gioco NUOVO (invio.esito di un gioco precedente non deve restare
  // a schermo su questo): un ref, non le dipendenze — `invio` e' un oggetto nuovo a ogni render
  // del genitore, e rimetterlo in una lista di dipendenze riarmerebbe l'effetto a ogni giro.
  const resetAlMontaggio = useRef(invio.onReset);
  resetAlMontaggio.current = invio.onReset;
  useEffect(() => {
    resetAlMontaggio.current();
  }, []);

  if (invio.stato === 'inviata') {
    return (
      <p className="py-3 text-center text-sm font-semibold text-success" role="status">
        {t('social.sfida_inviata', {
          defaultValue: 'Challenge sent to {{name}}, valid for 24 hours.',
          name: avversario,
        })}
      </p>
    );
  }

  const inviando = invio.stato === 'inviando';
  // 🔴 BLOCCANTE (revisione #487): senza una lista profili ARRIVATA non si offre l'invio come se
  // il profilo non servisse — la stessa regola di `LimuloRegistrationActions` sull'iscrizione ai
  // tornei (`attesaProfili`). «pronti» e' l'unico stato in cui «Invia» puo' accendersi.
  const statoProfili = invio.statoProfili(gioco.gameId);

  return (
    <>
      <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
        {t('social.duel_game_mode', { defaultValue: 'Game mode' })}
      </p>
      {stato.kind === 'caricamento' && (
        <p role="status" className="mb-5 flex items-center gap-2 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('social.sfida_modalita_caricamento', { defaultValue: 'Loading modes…' })}
        </p>
      )}
      {stato.kind === 'errore' && (
        <p role="alert" className="mb-5 text-sm text-fg-muted">
          {t('social.sfida_modalita_errore', { defaultValue: "Couldn't load the {{game}} modes.", game: gioco.nome })}
        </p>
      )}
      {stato.kind === 'ok' && modalita.length === 0 && (
        <p className="mb-5 text-sm text-fg-muted">
          {t('social.sfida_modalita_nessuna_inviabile', {
            defaultValue: 'No 1v1 mode to challenge {{game}} with yet.',
            game: gioco.nome,
          })}
        </p>
      )}
      {assi !== null &&
        sceltaAssi !== null &&
        // PONTE PROVVISORIO I-1317 (A1: la scelta «Senza tutor / Con tutor», solo backgammon) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda).
        (import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' && PonteAssiI1317Lazy && ponteI1317 !== null && eBackgammon && env.featureDmEnabled ? (
          <Suspense
            fallback={
              <SfidaAssiBackgammon
              assi={assi}
              scelta={sceltaAssi}
              onScegli={(asse, valore) => setSceltaAssiManuale(scegliSuAsse(assi, sceltaAssi, asse, valore))}
              disabled={inviando}
            />
            }
          >
            <PonteAssiI1317Lazy
              assi={assi}
              scelta={sceltaAssi}
              onScegli={(asse, valore) => setSceltaAssiManuale(scegliSuAsse(assi, sceltaAssi, asse, valore))}
              disabled={inviando}
            />
          </Suspense>
        ) : (
          <SfidaAssiBackgammon
            assi={assi}
            scelta={sceltaAssi}
            onScegli={(asse, valore) => setSceltaAssiManuale(scegliSuAsse(assi, sceltaAssi, asse, valore))}
            disabled={inviando}
          />
        ))}
      {assi === null && modalita.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {modalita.map((m) => (
            <Button
              key={m.key}
              variant="scelta"
              tono="incavo"
              peso="bold"
              pressed={modalitaScelta === m.key}
              onClick={() => setSceltaManuale(m.key)}
              disabled={inviando}
              className="after:h-9"
            >
              {m.nome}
            </Button>
          ))}
        </div>
      )}
      {statoProfili !== 'pronti' && (
        <div className="mb-3 flex items-center justify-between gap-2 text-xs leading-relaxed text-fg-muted">
          <p>
            {statoProfili === 'illeggibili'
              ? t('social.sfida_profili_illeggibili', { defaultValue: "Couldn't check your game profile." })
              : t('social.sfida_profili_lettura', { defaultValue: 'Checking your game profile…' })}
          </p>
          {statoProfili === 'illeggibili' && (
            <Button
              variant="neutro"
              size="sm"
              forma="pastiglia"
              peso="bold"
              onClick={invio.onRiprovaProfili}
              className={`shrink-0 ${RIPROVA_COMPATTO}`}
            >
              {t('social.sfida_riprova', { defaultValue: 'Retry' })}
            </Button>
          )}
        </div>
      )}
      {inviando && invio.collegando === true && (
        <p className="mb-3 text-sm text-fg-muted" role="status" data-testid="sfida-collegando-profilo">
          {t(CHIAVE_COLLEGANDO_PROFILO)}
        </p>
      )}
      {invio.stato === 'errore' && invio.esito !== null && <RifiutoSfida esito={invio.esito} />}
      {/* PONTE PROVVISORIO I-1317 (A2: con «Con tutor» l'invio e' il ponte; con «Senza tutor» il bottone di sempre) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda). */}
      {import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' &&
      PonteInvioI1317Lazy &&
      ponteI1317 !== null &&
      eBackgammon &&
      env.featureDmEnabled &&
      assi !== null &&
      sceltaAssi !== null ? (
        <Suspense
          fallback={
            <Button peso="bold" fullWidth disabled data-testid="ponte-i1317-invia-attesa">
              <Swords className="h-4 w-4" />
              {t('social.duel_send', { defaultValue: 'Send challenge' })}
            </Button>
          }
        >
          <PonteInvioI1317Lazy
            lunghezza={sceltaAssi.lunghezza}
            avversario={avversario}
            ponte={ponteI1317}
            bottone={
              <Button
                peso="bold"
                fullWidth
                disabled={modalitaScelta === null || inviando || statoProfili !== 'pronti'}
                onClick={() =>
                  modalitaScelta !== null && invio.onInvia({ gameId: gioco.gameId, gameModeKey: modalitaScelta })
                }
              >
                {inviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Swords className="h-4 w-4" />}
                {t('social.duel_send', { defaultValue: 'Send challenge' })}
              </Button>
            }
          />
        </Suspense>
      ) : (
        <Button
          peso="bold"
          fullWidth
          disabled={modalitaScelta === null || inviando || statoProfili !== 'pronti'}
          onClick={() =>
            modalitaScelta !== null && invio.onInvia({ gameId: gioco.gameId, gameModeKey: modalitaScelta })
          }
        >
          {inviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Swords className="h-4 w-4" />}
          {t('social.duel_send', { defaultValue: 'Send challenge' })}
        </Button>
      )}
    </>
  );
}

const NESSUNO: ReadonlySet<string> = new Set();

/**
 * Il pannello col suo catalogo: si monta solo mentre e' aperto, quindi `/v1/games` si chiede solo
 * quando serve. Il backgammon passa di qui come ogni altro gioco (T2-3, lotto C1): il ponte
 * dell'invito nativo QGammon non c'e' piu' lato invio, e chiudere il pannello non annulla niente,
 * perche' la sfida inviata non e' un invito aperto ma una sfida di piattaforma.
 */
export function SfidaSheetDaCatalogo({
  avversario,
  amicoId,
  preselezionaBackgammon,
  preselezionaClashRoyale,
  onPreferisciCodice,
  onClose,
}: {
  avversario: string;
  /** Il `sub` QSocials dell'amico: il destinatario della sfida (`target: {sub}`). */
  amicoId: string;
  /**
   * Apre gia' col backgammon scelto (T1-6): oggi solo il selettore d'amici della lobby backgammon
   * lo passa. Nessun effetto se il catalogo non porta il backgammon fra i giochi OFFERTI (cioe'
   * 1v1, `supportsOneVsOne`): il pannello si apre normale, sullo step «scegli il gioco».
   */
  preselezionaBackgammon?: boolean;
  /**
   * Apre gia' con Clash Royale scelto: oggi solo la chiave SFIDE della Mode Rail. Nessun effetto se il catalogo non porta
   * Clash Royale fra i giochi OFFERTI (1v1): il pannello si apre normale, sullo step «scegli il gioco». Il backgammon
   * ha la precedenza se passano tutte e due.
   */
  preselezionaClashRoyale?: boolean;
  /**
   * Il ripiego della lobby backgammon (lo STESSO del selettore d'amici, un riferimento solo):
   * chiude questo pannello e apre il flusso di sempre. Con `preselezionaBackgammon`, se il
   * backgammon non e' offerto (definitivamente), il pannello lo offre come uscita. Senza, nessuna
   * uscita.
   */
  onPreferisciCodice?: () => void;
  onClose: () => void;
}) {
  const q = useGetLimuloGamesQuery(undefined);
  const invioHook = useInvioSfidaPiattaforma(amicoId);
  // Serve SOLO alla preselezione (il gioco e la modalita'): cosa si OFFRE lo dice il catalogo.
  const giochiBackgammon = useMemo(() => backgammonGameIdsOf(q.data) ?? NESSUNO, [q.data]);
  const giochiCr = useMemo(() => crGameIdsOf(q.data) ?? NESSUNO, [q.data]);
  const catalogo = catalogoDellaSfida(q);
  const offertiBackgammon =
    catalogo.kind === 'ok' ? catalogo.giochi.filter((g) => giochiBackgammon.has(g.gameId)) : [];
  const offertiCr = catalogo.kind === 'ok' ? catalogo.giochi.filter((g) => giochiCr.has(g.gameId)) : [];
  // Il primo backgammon fra i giochi OFFERTI, non il primo del catalogo: con due backgammon e il
  // primo non 1v1 la preselezione non deve cadere su un gioco che il pannello non mostra.
  const giocoIniziale = preselezionaBackgammon ? offertiBackgammon[0]?.gameId : preselezionaClashRoyale ? offertiCr[0]?.gameId : undefined;
  // Le modalita' del backgammon preselezionato: la STESSA chiave di `InvioModalita` (stesso
  // `gameId`), quindi una GET sola. Senza preselezione, o senza un backgammon offerto, la query
  // salta.
  const qModi = useGetLimuloGameMatchModesQuery(giocoIniziale ?? '', {
    skip: giocoIniziale === undefined,
  });
  const modiBackgammon = modalitaInviabiliDi(qModi);
  const modiInErrore = giocoIniziale !== undefined && modiBackgammon.kind === 'errore';
  const modiSenzaInviabili =
    giocoIniziale !== undefined &&
    modiBackgammon.kind === 'ok' &&
    modiBackgammon.modalita.length === 0;
  // L'assenza e' DEFINITIVA solo a risposta arrivata: catalogo senza backgammon, catalogo in
  // errore, oppure backgammon offerto ma con le modalita' arrivate senza nessuna inviabile, o in
  // errore. Mentre uno dei due carica niente riga e niente bottone (niente lampi, poi il
  // backgammon compare).
  const assenzaDefinitiva =
    catalogo.kind === 'errore' ||
    (catalogo.kind === 'ok' && offertiBackgammon.length === 0) ||
    modiInErrore ||
    modiSenzaInviabili;
  // Un errore che riprovando puo' passare si ritenta, un elenco vuoto o un rifiuto (403, 424) no:
  // «Riprova» solo dove l'assenza nasce da un errore RIPROVABILE delle modalita', con la stessa
  // regola del catalogo (`catalogoRiprovabile`, che ha il suo «Riprova» nell'elenco dei giochi).
  const modiRiprovabili = modiInErrore && catalogoRiprovabile(limuloFailure(qModi.error));
  const uscitaBackgammon =
    preselezionaBackgammon && onPreferisciCodice !== undefined && assenzaDefinitiva
      ? { onPreferisciCodice, onRiprova: modiRiprovabili ? () => void qModi.refetch() : undefined }
      : null;
  const modalitaPredefinita = useMemo(
    () => (gameId: string) =>
      giochiBackgammon.has(gameId) ? BACKGAMMON_MODALITA_PREDEFINITA : null,
    [giochiBackgammon],
  );
  // I-459: lo STESSO insieme che da' la preselezione dice anche «questo gioco e' backgammon» (`profileType`).
  const eBackgammonDi = useMemo(() => (gameId: string) => giochiBackgammon.has(gameId), [giochiBackgammon]);

  // PONTE PROVVISORIO I-1317 (A3: il contesto del ponte, creato SOLO nel letterale) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda).
  return import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' ? (
    <PonteI1317Fornitore value={{ amicoId, onClose }}>
      <SfidaSheet
        avversario={avversario}
        catalogo={catalogo}
        invio={{
          stato: invioHook.stato,
          esito: invioHook.esito,
          collegando: invioHook.collegando,
          statoProfili: invioHook.statoProfili,
          onRiprovaProfili: invioHook.riprovaProfili,
          onGiocoScelto: invioHook.scegliGioco,
          onInvia: invioHook.invia,
          onReset: invioHook.reset,
        }}
        modalitaDi={modalitaDaCatalogo}
        modalitaPredefinita={modalitaPredefinita}
        eBackgammon={eBackgammonDi}
        uscitaBackgammon={uscitaBackgammon}
        giocoIniziale={giocoIniziale}
        onRiprova={() => void q.refetch()}
        onClose={onClose}
      />
    </PonteI1317Fornitore>
  ) : (
    <SfidaSheet
      avversario={avversario}
      catalogo={catalogo}
      invio={{
        stato: invioHook.stato,
        esito: invioHook.esito,
        collegando: invioHook.collegando,
        statoProfili: invioHook.statoProfili,
        onRiprovaProfili: invioHook.riprovaProfili,
        onGiocoScelto: invioHook.scegliGioco,
        onInvia: invioHook.invia,
        onReset: invioHook.reset,
      }}
      modalitaDi={modalitaDaCatalogo}
      modalitaPredefinita={modalitaPredefinita}
      eBackgammon={eBackgammonDi}
      uscitaBackgammon={uscitaBackgammon}
      giocoIniziale={giocoIniziale}
      onRiprova={() => void q.refetch()}
      onClose={onClose}
    />
  );
}
